import moment from "moment/moment";
import {DateFormats, inventoryCorrectionReasons} from "../../components/constants/securspace-constants";
import type {InventoryCorrection} from "../../types/InventoryCorrection";
import type {GroupedLocationInventoryActivity} from "../../types/GroupedLocationInventoryActivity";

/** A submitted correction (the API's InventoryCorrectionGetVO), narrowed to the fields getDates reads. */
interface CorrectionWithActivity extends Pick<InventoryCorrection, 'correctActivityDate' | 'reason'> {
  locationInventoryActivityGetVO: {
    activity: string;
    activityDate: string;
    otherActivityDate: string | null;
  } | null;
  oldActivityDate: string | null;
}

/** Original and revised check-in/check-out dates of a correction, formatted for display. */
interface CorrectionDates {
  originalCheckIn: string;
  revisedCheckIn: string;
  originalCheckOut: string;
  revisedCheckOut: string;
}

export const getDates = (correction: CorrectionWithActivity): CorrectionDates | undefined => {
  const {locationInventoryActivityGetVO, correctActivityDate, oldActivityDate, reason} = correction;

  if (locationInventoryActivityGetVO) {

    const {activity, activityDate, otherActivityDate} = locationInventoryActivityGetVO;

    const isCheckIn = (testActivity: string): boolean => testActivity === 'CHECK_IN';

    const formatDate = (date: string | null): string => {
      const dateMoment = moment(date);
      return dateMoment.isValid() ? dateMoment.format(DateFormats.DATE) : '-';
    }

    // Need this because oldActivityDate will only be set on corrections created after old_date column was created on Inventory Corrections table
    const nullSafeOriginalDate = oldActivityDate || correctActivityDate;

    const dateRevisionOnCheckIn = {      
      originalCheckIn: formatDate(nullSafeOriginalDate),
      revisedCheckIn: formatDate(correctActivityDate),
      originalCheckOut: formatDate(otherActivityDate),
      revisedCheckOut: formatDate(otherActivityDate),
    };

    const dateRevisionOnCheckOut = {      
      originalCheckIn: formatDate(otherActivityDate),
      revisedCheckIn: formatDate(otherActivityDate),
      originalCheckOut: formatDate(nullSafeOriginalDate),
      revisedCheckOut: formatDate(correctActivityDate),
    };

    const dateRevisionAssetLeftYard = {      
      originalCheckIn: formatDate(activityDate),
      revisedCheckIn: formatDate(activityDate),
      originalCheckOut: "N/A",
      revisedCheckOut: formatDate(correctActivityDate),
    };

    // if reason is asset left the yard, the correction will always be on a check in since the check out does not yet exist.
    if (isCheckIn(activity) && reason !== inventoryCorrectionReasons.ASSET_LEFT_THE_YARD) {
      return dateRevisionOnCheckIn;
    } else if (reason === inventoryCorrectionReasons.ASSET_LEFT_THE_YARD) {
      return dateRevisionAssetLeftYard;
    } else {
      return dateRevisionOnCheckOut;
    }

  }
};

export const shouldUseCheckOutActivity = (
  inventory: Partial<Pick<GroupedLocationInventoryActivity, 'ids' | 'checkInCorrectionId'>>,
  reason: string,
): boolean => {
  const {ids} = inventory;

  const reasonsForCheckOutCorrection: string[] = [inventoryCorrectionReasons.ASSET_CHECKED_OUT_ON_WRONG_DATE];

  if(inventory?.checkInCorrectionId) {
      reasonsForCheckOutCorrection.push(inventoryCorrectionReasons.ASSET_NUMBER_ENTERED_INCORRECTLY);
  }

  return ids?.length === 2 && reasonsForCheckOutCorrection.includes(reason);
      
};